/**
 * taskStore — Gerencia tarefas do usuário com sincronização bidirecional Google Tasks
 *
 * Consumidores: App, ActionBar, TaskPanel, PreferenciasModal, Preferencias
 * Dependências: googleTasksService
 * Persistência: localStorage (bigwa-tasks) + Google Tasks API
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { googleTasksService } from '../services/googleTasksService';
import { logger } from '../utils/logger';
import { bigwaToast } from '../utils/bigwaToast';

export interface BigwaTask {
  id: string;
  title: string;
  notes?: string;
  dueDate?: string;
  projectId: string;
  referenceId?: string;
  status: 'needsAction' | 'completed';
  createdAt: string;
  updatedAt: string;
  order: number;
  // Google Tasks sync
  googleTaskId?: string;
  syncedAt?: string;
}

interface TaskStore {
  tasks: BigwaTask[];
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncAt: string | null;
  googleTasksEnabled: boolean;
  
  // Actions
  addTask: (task: Omit<BigwaTask, 'id' | 'createdAt' | 'updatedAt' | 'order'>) => Promise<BigwaTask>;
  updateTask: (id: string, updates: Partial<BigwaTask>) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  toggleComplete: (id: string) => Promise<void>;
  reorderTasks: (projectId: string, activeId: string, overId: string) => void;
  
  // Google Tasks
  setGoogleTasksEnabled: (enabled: boolean) => void;
  syncWithGoogle: () => Promise<void>;
  
  // Queries
  getTasksByProject: (projectId: string) => BigwaTask[];
  getTasksByReference: (referenceId: string) => BigwaTask[];
  getPendingTasks: () => BigwaTask[];
  getCompletedTasks: () => BigwaTask[];
  getTasksDueToday: () => BigwaTask[];
  getOverdueTasks: () => BigwaTask[];
}

export const useTaskStore = create<TaskStore>()(
  persist(
    (set, get) => ({
      tasks: [],
      isLoading: false,
      isSyncing: false,
      lastSyncAt: null,
      googleTasksEnabled: false,

      addTask: async (taskData) => {
        const now = new Date().toISOString();
        
        // Calcular order (maior order do projeto + 1)
        const projectTasks = get().tasks.filter(t => t.projectId === taskData.projectId);
        const maxOrder = projectTasks.length > 0 
          ? Math.max(...projectTasks.map(t => t.order ?? 0)) 
          : -1;
        
        const newTask: BigwaTask = {
          ...taskData,
          id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: now,
          updatedAt: now,
          order: maxOrder + 1,
        };
        
        // Adicionar localmente primeiro
        set((state) => ({
          tasks: [newTask, ...state.tasks]
        }));

        // Sincronizar com Google Tasks se habilitado
        if (get().googleTasksEnabled) {
          try {
            const googleTaskId = await googleTasksService.createTask({
              title: newTask.title,
              notes: newTask.notes,
              dueDate: newTask.dueDate,
              projectId: newTask.projectId,
              referenceId: newTask.referenceId,
              bigwaTaskId: newTask.id,
            });
            
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id === newTask.id
                  ? { ...t, googleTaskId, syncedAt: new Date().toISOString() }
                  : t
              )
            }));
            
            newTask.googleTaskId = googleTaskId;
          } catch (error) {
            logger.error('TaskStore', 'Erro ao sincronizar com Google Tasks:', error);
            bigwaToast.notifyError('Tarefa criada localmente, mas não foi possível sincronizar com Google Tasks.');
          }
        }

        return newTask;
      },

      updateTask: async (id, updates) => {
        const task = get().tasks.find(t => t.id === id);
        
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id
              ? { ...t, ...updates, updatedAt: new Date().toISOString() }
              : t
          )
        }));

        // Sincronizar com Google Tasks se habilitado e tem googleTaskId
        if (get().googleTasksEnabled && task?.googleTaskId) {
          try {
            await googleTasksService.updateTask(task.googleTaskId, {
              title: updates.title,
              notes: updates.notes,
              dueDate: updates.dueDate,
              projectId: updates.projectId ?? task.projectId,
              referenceId: updates.referenceId,
              bigwaTaskId: id,
            });
            
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id === id
                  ? { ...t, syncedAt: new Date().toISOString() }
                  : t
              )
            }));
          } catch (error) {
            logger.error('TaskStore', 'Erro ao atualizar no Google Tasks:', error);
            bigwaToast.notifyError('Tarefa atualizada localmente, mas a sincronização com Google Tasks falhou.');
          }
        }
      },

      deleteTask: async (id) => {
        const task = get().tasks.find(t => t.id === id);
        
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== id)
        }));

        // Deletar do Google Tasks se habilitado e tem googleTaskId
        if (get().googleTasksEnabled && task?.googleTaskId) {
          try {
            await googleTasksService.deleteTask(task.googleTaskId);
          } catch (error) {
            logger.error('TaskStore', 'Erro ao deletar do Google Tasks:', error);
            bigwaToast.notifyError('Tarefa removida localmente, mas a sincronização com Google Tasks falhou.');
          }
        }
      },

      toggleComplete: async (id) => {
        const task = get().tasks.find(t => t.id === id);
        if (!task) return;
        
        const newStatus = task.status === 'completed' ? 'needsAction' : 'completed';
        
        set((state) => ({
          tasks: state.tasks.map((t) =>
            t.id === id
              ? { ...t, status: newStatus, updatedAt: new Date().toISOString() }
              : t
          )
        }));

        // Sincronizar com Google Tasks
        if (get().googleTasksEnabled && task.googleTaskId) {
          try {
            if (newStatus === 'completed') {
              await googleTasksService.completeTask(task.googleTaskId);
            } else {
              await googleTasksService.uncompleteTask(task.googleTaskId);
            }
            
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id === id
                  ? { ...t, syncedAt: new Date().toISOString() }
                  : t
              )
            }));
          } catch (error) {
            logger.error('TaskStore', 'Erro ao toggle no Google Tasks:', error);
            bigwaToast.notifyError('Status alterado localmente, mas a sincronização com Google Tasks falhou.');
          }
        }
      },

      reorderTasks: (projectId, activeId, overId) => {
        if (activeId === overId) return;
        
        set((state) => {
          const projectTasks = state.tasks
            .filter(t => t.projectId === projectId && t.status === 'needsAction')
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          
          const oldIndex = projectTasks.findIndex(t => t.id === activeId);
          const newIndex = projectTasks.findIndex(t => t.id === overId);
          
          if (oldIndex === -1 || newIndex === -1) return state;
          
          const reordered = [...projectTasks];
          const [moved] = reordered.splice(oldIndex, 1);
          reordered.splice(newIndex, 0, moved);
          
          const updatedIds = new Map<string, number>();
          reordered.forEach((task, index) => {
            updatedIds.set(task.id, index);
          });
          
          return {
            tasks: state.tasks.map(t => {
              if (updatedIds.has(t.id)) {
                return { ...t, order: updatedIds.get(t.id)!, updatedAt: new Date().toISOString() };
              }
              return t;
            })
          };
        });
      },

      setGoogleTasksEnabled: (enabled) => {
        set({ googleTasksEnabled: enabled });
        if (!enabled) {
          googleTasksService.clearCache();
        }
      },

      // Sincronização BIDIRECIONAL com Google Tasks
      syncWithGoogle: async () => {
        if (!get().googleTasksEnabled) return;
        
        set({ isSyncing: true });
        
        try {
          const googleTasks = await googleTasksService.getAllTasks();
          const localTasks = get().tasks;
          

          // 1. BIGWA → GOOGLE: Criar tarefas locais que não existem no Google
          for (const localTask of localTasks) {
            if (!localTask.googleTaskId) {
              try {
                const googleTaskId = await googleTasksService.createTask({
                  title: localTask.title,
                  notes: localTask.notes,
                  dueDate: localTask.dueDate,
                  projectId: localTask.projectId,
                  referenceId: localTask.referenceId,
                  bigwaTaskId: localTask.id,
                });
                
                set((state) => ({
                  tasks: state.tasks.map((t) =>
                    t.id === localTask.id
                      ? { ...t, googleTaskId, syncedAt: new Date().toISOString() }
                      : t
                  )
                }));
                
              } catch (error) {
                logger.error('TaskStore', `Erro ao sincronizar tarefa: ${localTask.id}`, error);
              }
            }
          }
          
          // 2. GOOGLE → BIGWA: Atualizar tarefas locais com mudanças do Google
          for (const googleTask of googleTasks) {
            if (googleTask.metadata?.bigwaTaskId) {
              const localTask = localTasks.find(t => t.id === googleTask.metadata?.bigwaTaskId);
              
              if (localTask) {
                const hasChanges = 
                  localTask.status !== googleTask.status ||
                  localTask.title !== googleTask.title ||
                  localTask.dueDate !== googleTask.dueDate ||
                  (localTask.notes || '') !== (googleTask.notes || '');
                
                if (hasChanges) {
                  
                  set((state) => ({
                    tasks: state.tasks.map((t) =>
                      t.id === localTask.id
                        ? { 
                            ...t, 
                            status: googleTask.status,
                            title: googleTask.title,
                            dueDate: googleTask.dueDate,
                            notes: googleTask.notes || t.notes,
                            syncedAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                          }
                        : t
                    )
                  }));
                }
              }
            }
          }
          
          // 3. Detectar tarefas deletadas no Google
          const googleTaskIds = new Set(googleTasks.map(t => t.googleTaskId));
          const tasksWithGoogleId = localTasks.filter(t => t.googleTaskId);
          
          for (const localTask of tasksWithGoogleId) {
            if (!googleTaskIds.has(localTask.googleTaskId!)) {
              set((state) => ({
                tasks: state.tasks.filter(t => t.id !== localTask.id)
              }));
            }
          }
          
          set({ 
            isSyncing: false,
            lastSyncAt: new Date().toISOString()
          });
          
        } catch (error) {
          logger.error('TaskStore', 'Erro na sincronização:', error);
          set({ isSyncing: false });
        }
      },

      getTasksByProject: (projectId) => {
        return get().tasks
          .filter((task) => task.projectId === projectId)
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      },

      getTasksByReference: (referenceId) => {
        return get().tasks.filter((task) => task.referenceId === referenceId);
      },

      getPendingTasks: () => {
        return get().tasks
          .filter((task) => task.status === 'needsAction')
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      },

      getCompletedTasks: () => {
        return get().tasks.filter((task) => task.status === 'completed');
      },

      getTasksDueToday: () => {
        const today = new Date().toISOString().split('T')[0];
        return get().tasks.filter(
          (task) => task.dueDate === today && task.status === 'needsAction'
        );
      },

      getOverdueTasks: () => {
        const today = new Date().toISOString().split('T')[0];
        return get().tasks.filter(
          (task) =>
            task.dueDate &&
            task.dueDate < today &&
            task.status === 'needsAction'
        );
      },
    }),
    {
      name: 'bigwa-tasks',
    }
  )
);

export default useTaskStore;
